import { gql, useQuery } from '@apollo/client';
import SubtitlesOutlinedIcon from '@mui/icons-material/SubtitlesOutlined';
import Skeleton from '@mui/material/Skeleton';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useNavigate } from '@tanstack/react-router';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { HOME_PATH, SEARCH_PARAM } from 'src/constants';
import { timeCodeFromSecondsAssetIn } from 'src/dynamoQueries/timecodes';
import HighlightedFragment from '../HighlightedText/HighlightedFragment';
import HighlightedText from '../HighlightedText/HighlightedText';
import { frameRateIsDropFrame, timecodeIsDropFrame } from '../VideoJs/utils';

const useStyles = makeStyles((theme: Theme) => ({
  timecodeLoading: {
    width: 80,
    height: 14,
    borderRadius: 3,
  },
}));

interface LinguisticItem {
  text: string;
}

interface LinguisticProps {
  ling: string;
}

const Linguistic: React.FC<LinguisticProps> = ({ ling }) => {
  const customSearchURL = new URLSearchParams(window.location.search);
  const searchTerm = customSearchURL.get(SEARCH_PARAM);
  const hasWords = customSearchURL.getAll('include').filter((a) => !a.includes(' '));
  const hasPhrase = customSearchURL
    .getAll('include')
    .filter((a) => a.includes(' '))
    .toString()
    .replace('+', ' ');
  return (
    <span>
      <HighlightedText highlightTexts={[searchTerm || '', hasPhrase, ...hasWords]} text={ling} />
    </span>
  );
};

interface ExpressionData {
  startTime: number;
  linguistics: LinguisticItem[];
  highlighted_text?: string;
}

interface ItemMeta {
  id: string;
}

interface ExpressionProps {
  expression: ExpressionData;
  meta: ItemMeta;
  className?: string;
  textClassName?: string;
}

const Expression: React.FC<ExpressionProps> = ({ expression, meta, className, textClassName }) => {
  const navigate = useNavigate();
  const customSearchURL = new URLSearchParams(window.location.search);
  const searchTerm = customSearchURL.get(SEARCH_PARAM);
  const hasWords = customSearchURL.getAll('include').filter((a) => !a.includes(' '));
  const hasPhrase = customSearchURL
    .getAll('include')
    .filter((a) => a.includes(' '))
    .toString()
    .replace('+', ' ');

  const onClickTranscript = (): void => {
    navigate({
      to: `${HOME_PATH}/media-page/${meta.id}?t=${expression.startTime}&&tab=Transcript&&${hasWords.map(
        (a) => `include=${a}&`,
      )}${searchTerm && `&term=${searchTerm}`}${hasPhrase && `&include=${hasPhrase}`}`,
    });
  };
  const fullExpression = expression.linguistics.map(({ text }) => text).join('');

  return (
    <button type="button" onClick={onClickTranscript} className={className}>
      <span className={textClassName}>
        {expression.highlighted_text ? (
          <HighlightedFragment fragment={expression.highlighted_text} />
        ) : (
          <Linguistic ling={fullExpression} />
        )}
      </span>
    </button>
  );
};

export const noUndefinedOrNulls = (vals: (unknown | undefined | null)[] = []): boolean => {
  return !(vals.filter((x) => x === undefined || x === null).length >= 1);
};

export const isDropFrame = (framerate: number | string, inpoint: string): boolean => {
  let timecodeDropframe = timecodeIsDropFrame(inpoint);
  const framerateDropframe = frameRateIsDropFrame(framerate);
  if (timecodeDropframe && !framerateDropframe) {
    console.log(`Timecode ${inpoint} from asset in point is drop-frame, but framerate ${framerate} is not.
    Forcing to non drop-frame.`);
    timecodeDropframe = false;
  }

  return timecodeDropframe && framerateDropframe;
};

interface TimecodeDisplayProps {
  assetId: string;
  seconds: number;
  className?: string;
}

interface TimecodeFromSecondsData {
  timecodeFromSecondsAssetIn: {
    timecode: string;
  };
}

export const TimecodeDisplay: React.FC<TimecodeDisplayProps> = ({ assetId, seconds, className }) => {
  const fallback = '00:00:00:00';
  const classes = useStyles();

  const timeCodeFromSecondsBuildQuery = () => {
    const tcsQuery = timeCodeFromSecondsAssetIn(true);
    const gQuery = jsonToGraphQLQuery(tcsQuery, { pretty: true });
    return gql(gQuery);
  };

  const { data, loading, error } = useQuery<TimecodeFromSecondsData>(timeCodeFromSecondsBuildQuery(), {
    variables: { assetId, seconds },
  });

  if (loading) return <Skeleton variant="text" animation="wave" className={classes.timecodeLoading} />;
  if (error) return <div className={className}>{fallback}</div>;

  return <div className={className}>{data?.timecodeFromSecondsAssetIn?.timecode ?? fallback}</div>;
};

interface TranscriptResultsProps {
  expressions: ExpressionData[];
  item: ItemMeta;
  classes: {
    transcriptHeader: string;
    transcriptExpression: string;
    transcriptTimecode: string;
    transcriptText: string;
  };
}

export const TranscriptResults: React.FC<TranscriptResultsProps> = ({ expressions, item, classes }) => {
  return (
    <>
      <div className={classes.transcriptHeader}>
        <SubtitlesOutlinedIcon />
        <span>Transcript</span>
      </div>
      {expressions.map((expr, idx) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: expressions may have duplicate start times
        <div key={`expr${idx}`} className={classes.transcriptExpression}>
          <TimecodeDisplay assetId={item?.id} seconds={expr?.startTime} className={classes.transcriptTimecode} />
          <Expression
            meta={item}
            expression={expr}
            className={classes.transcriptText}
            textClassName={classes.transcriptText}
          />
        </div>
      ))}
    </>
  );
};
